import React from 'react'
import {Outlet, useParams} from 'react-router-dom'
import {useMutation, useQuery} from '@apollo/client'
import {
  Button,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@material-ui/core'
import Skeleton from 'react-loading-skeleton'
import LinesEllipsis from 'react-lines-ellipsis/lib/loose'
import {
  SidebarMenu,
  SidebarNavItem,
} from '../../../../components/contributor/SidebarMenu'
import ContributorCreatorDetailLayout from '../../../../components/contributor/layout/ContributorCreatorDetailLayout'
import {
  GET_CONTRIBUTOR_BOOK_DETAILS,
  GET_CONTRIBUTOR_BOOK_DETAILS_PROGRESS,
} from '../../../../graphql/queries'
import {USER_ID} from '../../../../utils/globals'
import {INSTRUCTOR_CONTEXT} from '../../../../utils/contributor-helpers'
import {CONTRIBUTOR_UPDATE_BOOK} from '../../../../graphql/mutations/contributor/books.mutation'
import {useSnackbar} from 'notistack'

export interface BookPageContext {
  isbn?: string
}

const enum BookStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  DRAFT = 'draft',
}

const BookStatusColors: {[key: string]: string | undefined} = {
  [BookStatus.DRAFT]: undefined,
  [BookStatus.ACTIVE]: '#4CAF50',
  [BookStatus.PENDING]: '#EF4D5E',
} as const

type BookProps = {
  type: string
}

const ContributorBookDetailPageLayout = ({type}: BookProps) => {
  const {enqueueSnackbar} = useSnackbar()
  const {bookSlug} = useParams()
  const [updatingData, setUpdatingData] = React.useState(false)

  const {data, refetch} = useQuery(GET_CONTRIBUTOR_BOOK_DETAILS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      isbn: bookSlug,
      owner: USER_ID,
    },
    context: INSTRUCTOR_CONTEXT,
  })

  const {data: bookProgress} = useQuery(GET_CONTRIBUTOR_BOOK_DETAILS_PROGRESS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      isbn: bookSlug,
    },
    context: INSTRUCTOR_CONTEXT,
  })

  const [updateBook] = useMutation(CONTRIBUTOR_UPDATE_BOOK, {
    context: INSTRUCTOR_CONTEXT,
  })

  const onUpdateBookStatus = (status: string) => {
    if (!status) {
      return
    }
    setUpdatingData(true)

    updateBook({
      variables: {
        isbn: bookSlug,
        objects: {
          status,
        },
      },
    })
      .then(() => {
        refetch()
        setUpdatingData(false)
      })
      .catch(() => {
        enqueueSnackbar(`Failed to update status`, {variant: 'error'})
        setUpdatingData(false)
      })
  }

  const book = data?.academy_books[0]
  const progress = bookProgress?.getProgressBookDetail

  const Image = book && book.thumbnail ? 'img' : 'div'

  const context = React.useMemo(() => {
    const object: BookPageContext = {
      isbn: bookSlug,
    }

    return object
  }, [bookSlug])

  React.useEffect(() => {
    window.scrollTo({top: 0, behavior: 'auto'})
  }, [])

  if (data && data.academy_books.length < 1) {
    return (
      <ContributorCreatorDetailLayout
        label={`${type === 'ebooks' ? 'E-Book' : 'Audiobook'} Detail`}
        to={`../${type}`}
      >
        <div>
          {`${type === 'ebooks' ? 'E-Book' : 'Audiobook'} Detail`} not found
        </div>
      </ContributorCreatorDetailLayout>
    )
  }
  return (
    <ContributorCreatorDetailLayout
      label={`${type === 'ebooks' ? 'E-Book' : 'Audiobook'} Detail`}
      to={`../${type}`}
    >
      <Paper className="flex items-stretch gap-12 p-6">
        <div className="my-2 flex grow flex-col justify-between">
          <div>
            {book ? (
              <LinesEllipsis
                component={Typography}
                text={book.title}
                maxLine={3}
                color="primary"
                className="mb-1 text-lg font-bold"
              />
            ) : (
              <p className="mb-1 text-lg">
                <Skeleton width="50%" />
              </p>
            )}

            <Typography
              color="textSecondary"
              className="mb-4 text-sm capitalize"
              style={{color: book && BookStatusColors[book.status]}}
            >
              {book ? book.status : <Skeleton width={45} />}
            </Typography>
          </div>

          <div>
            {!book ? null : book.status === BookStatus.DRAFT ? (
              <>
                <Typography className="mb-2">
                  Progress {Math.round(progress?.total || 0)}%
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={progress?.total || 0}
                  color="secondary"
                  className="h-2 grow rounded"
                  classes={{bar: 'rounded'}}
                />

                <div className="mt-6 flex gap-4">
                  <Button
                    color="primary"
                    variant="outlined"
                    disabled={updatingData}
                    onClick={() => onUpdateBookStatus('draft')}
                  >
                    Save Draft
                  </Button>

                  <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={updatingData}
                    onClick={() => onUpdateBookStatus('pending')}
                  >
                    Publish
                  </Button>
                </div>
              </>
            ) : book.status === BookStatus.PENDING ? (
              <div className="mt-6 flex gap-4">
                <Button
                  color="inherit"
                  variant="outlined"
                  className={!updatingData ? 'text-red-500' : ''}
                  disabled={updatingData}
                  onClick={() => onUpdateBookStatus('draft')}
                >
                  Unpublish
                </Button>
              </div>
            ) : book.status === BookStatus.ACTIVE ? (
              <div className="mt-6 flex gap-4">
                <Button
                  color="inherit"
                  variant="outlined"
                  className="text-red-500"
                  disabled={updatingData}
                  onClick={() => onUpdateBookStatus('pending')}
                >
                  Unpublish
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <Image
          alt={book?.title}
          src={book?.thumbnail}
          className="aspect-video w-96 shrink-0 rounded bg-gray-200 object-cover"
        />
      </Paper>

      <div className="grid items-baseline gap-8 lg:grid-cols-[320px_minmax(0,_1fr)]">
        <SidebarMenu>
          <SidebarNavItem to="." end>
            Dashboard
          </SidebarNavItem>
          <SidebarNavItem to="manage">Manage</SidebarNavItem>
          <SidebarNavItem to="learners">Learner</SidebarNavItem>
          <SidebarNavItem to="earnings">Earnings</SidebarNavItem>
          <SidebarNavItem to="reviews">Review</SidebarNavItem>

          <Divider />

          <Button
            color="secondary"
            variant="contained"
            disableElevation
            className="mx-4 my-2"
          >
            Preview My E-Book
          </Button>
        </SidebarMenu>

        <Outlet context={context} />
      </div>
    </ContributorCreatorDetailLayout>
  )
}

export default ContributorBookDetailPageLayout
